import React, { useMemo } from 'react';
import ResultLinks from './ResultLinks';
import ResultSelect from './ResultSelect';
import { useTable, useSortBy } from 'react-table';
import { redirectIFrame } from 'helpers/iframeHelpers';
import { sortSearchResults } from '../../../actions/objectActions';

const VIEW_PREDICATE = 'http://www.profium.com/archive/view';
/**
 * SAHKE2-43, SPEC-318
 * Uses react-table to build a dynamic table with sortable columns
 * https://react-table.tanstack.com/docs/quick-start
 */
const SearchResultsTable = ( {objects, displayLink, 
  addToCart, removeFromCart, addLink, selectable, orderByPredicate, folderUri, toggleSorting, isPublicUser, isCart }) => {

  // predicate -> label
  const labelMap = useMemo(() => getLabelsByPredicate(objects), [objects]);

  // predicates for sorting
  const sortPredicatesMap = useMemo(() => getSortPredicatesMapped(objects), [objects]);

  // unique set of labels
  const labels = useMemo(() => getLabelsInOrder(objects), [labelMap]);

  // columns for react-table
  const columns = useMemo(() => {
    const columns = {};

    for (const [predicate, label] of Object.entries(labelMap)) {
      if (!columns[label]) {
        columns[labels.indexOf(label)] = {
          Header: label,
          accessor: label,
          predicate
        };
      }
    }

    return Object.values(columns);
  }, [labels]);

  // populate rows for columns
  const data = useMemo(() => {
    const rows = [];
    objects.forEach(object => {
      const row = {};
      labels.forEach((label, index) => {
        const field = object.fields.find(field => field.label === labels[index]);
        row[label] = field ? field.values.join(', ') : '';
      });
      rows.push(row);
    });

    return rows;
  }, [columns]);

  // Sort by "orderByPredicate" prop if possible, otherwise first column
  const sortBy = useMemo(() =>  {
    let predicate = '';
    if (orderByPredicate) {
      predicate = labelMap[orderByPredicate];
    } else {
      predicate = labels[0];
      orderByPredicate = labels[0];
    }
    return [{ id: predicate, desc: true }];
  }, [columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headers,
    rows,
    prepareRow,
  } = useTable({ columns, data, state: { sortBy }}, useSortBy);

  const sortColumn = column => {
    if (toggleSorting === 'true') {
      sortTable(false, column);
    } else {
      sortTable(true, column);
    }

    window.sortByPredicate = column.predicate;
  };

  if (!objects || objects.length < 1) {
    return null;
  }

  const sortTable = (toggleSorting, column) =>{
    const predicate = sortPredicatesMap[column.predicate];
    if (folderUri != null && folderUri.length > 0) {
      redirectIFrame('displayFolder.do?uri='+folderUri+'&orderBy='+predicate+'&ascending='+toggleSorting + '&sorting=true');
    } else {
      let asc = window.sessionStorage.getItem('isAscending');
      asc = asc ==='false' ? 'true' : 'false'; 
      window.sessionStorage.setItem('isAscending', asc);
      sortSearchResults(predicate, asc);
    }
  };

  return (
    <table {...getTableProps()} className="result-pane result-pane--tabular"
      cellSpacing="0" cellPadding="0" border="0" >
      <thead>
        {
          <tr>
            <th className="table_header" />{/* Empty headers for buttons */}
            <th className="table_header" />
            {headers.map((column, key) => (
              <th key={key}
                {...column.getHeaderProps()}
                className="table_header"
                style={{textAlign: 'left'}}
              >
              { isCart &&
                  <span>
                    {column.render('Header')}
                  </span>
              }
              { !isCart &&
                <span
                  {...column.getSortByToggleProps()}
                  onClick={() => sortColumn(column)}
                >
                  {column.render('Header')}
				          {getSortArrow(toggleSorting, column.predicate, orderByPredicate, key, headers)}
                </span>
              }
              </th>
            ))}
          </tr>
        }
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()} className={`content-row ${i % 2 !== 0 ? 'shaded' : ''}`} key={i}>
              <td className="table-data">
                <ResultSelect selectable={selectable && !addLink} object={objects[i]} />
              </td>
              <td className="table_data">
                <ResultLinks object={objects[i]} displayLink={displayLink}
                  addToCart={addToCart} removeFromCart={removeFromCart} addLink={addLink} image={objects[i].image} isPublicUser={isPublicUser} />
              </td>
              {row.cells.map((cell, i) => (
                <td {...cell.getCellProps()} className="table_data" key={i}>
                  {cell.render('Cell')}
                </td>
              )
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/**
 * 
 * @param {*} toggleSorting 
 * @param {*} predicate 
 * @param {*} orderByPredicate 
 * @param {*} key - to check if it's the first column
 * @param {*} headers 
 * @returns renders sorting arrow if the data is sorted by the column. Default is first column
 */
const getSortArrow = (toggleSorting, predicate, orderByPredicate, key, headers) => {
  let isCurrent = false;
  const splitPredicates = orderByPredicate.split('->');
  splitPredicates.forEach((p) => {
    if (predicate === p) {
      isCurrent = true;
    }
  });
  if (key === 0 && isDefaultSort(headers, splitPredicates)) {
    isCurrent = true;
  }
  if (isCurrent) {
    if (!toggleSorting) {
      toggleSorting = window.sessionStorage.getItem('isAscending');
    }
    if (toggleSorting === 'true') {
      return <span> &#9650;</span>;
    } else {
      return <span> &#9660;</span>;
    }
  }
  return '';
};

/**
 * 
 * @param {*} headers 
 * @param {*} splitPredicates 
 * @returns determines whether first column should be used when no column 
 * is picked by the user
 */
const isDefaultSort = (headers, splitPredicates) =>  {
  if (!splitPredicates || !headers) {
    return true;
  }
  const value = !headers.some((column) => {
    return splitPredicates.some((p) => {
      if (column.predicate === p) {
        return true;
      }
    });
  });
  return value;
};

const getLabelsByPredicate = objects => {
  const labels = {};
  for (let object of objects) {
    for (let field of object.fields) {
      labels[field.predicate] = field.label; 
    }
  }
  return labels;
};

const getSortPredicatesMapped = objects => {
  const predicates = {};
  for (let object of objects) {
    for (let field of object.fields) {
      if (field?.props?.orderByPredicate) {
        predicates[field.predicate] = field.props.orderByPredicate.includes(',') 
          ? field.props.orderByPredicate.join(',')
          : field.props.orderByPredicate;
      } else {
        predicates[field.predicate] = field.predicate; 
      }
    }
  }
  predicates['this'] = VIEW_PREDICATE;
  return predicates;
};

const getLabelsInOrder = objects => {
  const labels = [];
  for (let object of objects) {
    let fields = object.fields;
    for (let field of fields) {
      if (labels.includes(field.label)) {
        continue;
      }

      let index = fields.indexOf(field);
      if (index === 0) {
        labels.splice(0, 0, field.label);
      } else {
        let previousLabel = fields[index - 1].label;
        let previousLabelIndex = labels.indexOf(previousLabel);
        labels.splice(previousLabelIndex + 1, 0, field.label);
      }
    }
  }
  return labels;
};
export default SearchResultsTable;